import React, { useEffect, useState, useRef, useCallback, ReactNode } from 'react';

import LocalizedLink from '../components/atoms/LocalizedLink';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useTranslation } from 'react-i18next';
import PageLayout from '../components/templates/PageLayout';
import Container from '../components/atoms/Container';
import Button from '../components/atoms/Button';
import SEO from '../components/atoms/SEO';
import VideoPlayer from '../components/molecules/VideoPlayer';
import { AcademicCapIcon, UserGroupIcon, BookOpenIcon } from '@heroicons/react/24/solid';
import CalendarIcon from '@heroicons/react/24/outline/CalendarIcon';
import NewspaperIcon from '@heroicons/react/24/outline/NewspaperIcon';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { Autoplay, Pagination, Navigation, EffectFade } from 'swiper/modules';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import 'swiper/css/effect-fade';
import '../components/molecules/HomeCarousel.css';
import '../components/molecules/HomeCarousel.rtl.css';

// Helper function to check if we should show an image for this article
const shouldShowImage = (item: any): boolean => {
  // Only show image if the item has an image field and it's not empty
  return !!(item.image && item.image.trim() !== '');
};


interface AnimatedSectionProps {
  children: ReactNode;
  delay?: number;
  className?: string;
}

const AnimatedSection: React.FC<AnimatedSectionProps> = ({ children, delay = 0, className = '' }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: {
          opacity: 1,
          y: 0,
          transition: {
            duration: 0.6,
            delay: delay,
            ease: [0.22, 1, 0.36, 1],
          },
        },
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const CountUp: React.FC<{ end: number; suffix?: string; duration?: number }> = ({ end, suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const hasAnimated = useRef(false);

  const animate = useCallback(() => {
    if (hasAnimated.current) return;
    hasAnimated.current = true;
    const startTime = performance.now();
    const step = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };
    requestAnimationFrame(step);
  }, [end, duration]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) animate(); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [animate]);

  return <span ref={ref}>{count}{suffix}</span>;
};

const HomePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const swiperRef = React.useRef<SwiperType | null>(null);

  // Effect to handle language/direction changes
  useEffect(() => {
    // When language changes, update the Swiper instance
    if (swiperRef.current) {
      // Force update on direction change
      setTimeout(() => {
        if (swiperRef.current) {
          swiperRef.current.update();
          swiperRef.current.autoplay.start();
        }
      }, 100);
    }
  }, [i18n.language]);

  const features = [
    {
      name: t('home.features.features.academicExcellence.title'),
      description: t('home.features.features.academicExcellence.description'),
      icon: AcademicCapIcon,
      link: '/high-school',
      color: 'from-blue-500 to-indigo-600',
    },
    {
      name: t('home.features.features.robotics.title'),
      description: t('home.features.features.robotics.description'),
      icon: BookOpenIcon,
      link: '/academics#robotics',
      color: 'from-emerald-500 to-teal-600',
    },
    {
      name: t('home.features.features.diversity.title'),
      description: t('home.features.features.diversity.description'),
      icon: UserGroupIcon,
      link: '/about',
      color: 'from-amber-500 to-orange-600',
    },
  ];

  const stats = [
    { value: 200, suffix: '+', label: t('home.stats.students') },
    { value: 40, suffix: '+', label: t('home.stats.staff') },
    { value: 26, suffix: '+', label: t('home.stats.nationalities') },
    { value: 18, suffix: '', label: t('home.stats.classrooms') },
  ];

  // Get events from translations
  const eventsList = i18n.t('newsPage.eventsList', { returnObjects: true }) as Array<{
    id: number;
    title: string;
    date: string;
    time: string;
    location: string;
  }>;

  // Filter out past events and get upcoming events only
  const getUpcomingEvents = (events: typeof eventsList) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison
    
    return events.filter(event => {
      // Parse the event date - handle different date formats
      let eventDate: Date;
      
      if (event.date.includes(',')) {
        // Format: "October 31, 2025" or "31 octobre 2025"
        eventDate = new Date(event.date);
      } else {
        // Format: "October 31" or "31 octobre" - assume current year
        const currentYear = today.getFullYear();
        eventDate = new Date(`${event.date}, ${currentYear}`);
      }
      
      // Handle date ranges (e.g., "March 14-23, 2026" or "December 18, 2025-January 6, 2026")
      const hasMonthName = /(January|February|March|April|May|June|July|August|September|October|November|December|janvier|février|mars|avril|mai|juin|juillet|août|septembre|octobre|novembre|décembre)/i;
      if (event.date.includes('-') && hasMonthName.test(event.date) && !event.date.match(/^\d{4}-\d{2}-\d{2}/)) {
        // Check for "Month Day-Day, Year" format (e.g., "March 14-23, 2026")
        const sameMonthMatch = event.date.match(/^(\w+)\s+(\d+)-(\d+),?\s*(\d{4})$/);
        if (sameMonthMatch) {
          const endDate = new Date(`${sameMonthMatch[1]} ${sameMonthMatch[3]}, ${sameMonthMatch[4]}`);
          endDate.setHours(23, 59, 59, 999);
          return today <= endDate;
        }

        // Check for cross-month ranges (e.g., "December 18, 2025-January 6, 2026")
        const parts = event.date.split('-');
        if (parts.length >= 2) {
          let startDateStr = parts[0].trim();
          let endDateStr = parts.slice(1).join('-').trim();

          let startDate = new Date(startDateStr);
          if (isNaN(startDate.getTime())) {
            startDate = new Date(`${startDateStr}, ${today.getFullYear()}`);
          }
          startDate.setHours(0, 0, 0, 0);

          let endDate = new Date(endDateStr);
          if (isNaN(endDate.getTime())) {
            endDate = new Date(`${endDateStr}, ${startDate.getFullYear()}`);
          }
          endDate.setHours(23, 59, 59, 999);

          return today <= endDate;
        }
      }
      
      // Check if event date is today or in the future
      eventDate.setHours(0, 0, 0, 0);
      return eventDate >= today;
    });
  };

  // Filter to show specific upcoming events, sorted by date
  const specificEventIds = [5, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19];
  const specificEvents = eventsList.filter(event => specificEventIds.includes(event.id));
  const upcomingSpecific = getUpcomingEvents(specificEvents);

  const getEventSortDate = (event: typeof eventsList[0]): Date => {
    if (event.date.includes('-') && event.date.match(/(January|February|March|April|May|June|July|August|September|October|November|December)/i)) {
      // Handle "Month Day-Day, Year" format (e.g., "March 14-23, 2026") — use start date
      const sameMonthMatch = event.date.match(/^(\w+)\s+(\d+)-(\d+),?\s*(\d{4})$/);
      if (sameMonthMatch) {
        return new Date(`${sameMonthMatch[1]} ${sameMonthMatch[2]}, ${sameMonthMatch[4]}`);
      }
      // Handle cross-month ranges — use start date
      const parts = event.date.split('-');
      const startDate = new Date(parts[0].trim());
      if (!isNaN(startDate.getTime())) return startDate;
      return new Date(event.date);
    }
    return new Date(event.date);
  };

  const sortedEvents = upcomingSpecific.sort((a, b) => {
    return getEventSortDate(a).getTime() - getEventSortDate(b).getTime();
  });

  // Limit to 4 events on homepage
  const recentEventsList = sortedEvents.slice(0, 4);

  // Get news items from translations
  const newsItems = i18n.t('newsPage.newsItems', { returnObjects: true }) as Array<{
    id: number;
    title: string;
    date: string;
    category: string;
    image?: string;
    video?: string;
    gallery?: Array<{ src: string; alt: string; caption: string }>;
    summary: string;
    content: string;
    isLatest?: boolean;
  }>;

  // Sort news items by date (newest first)
  const sortedNewsItems = [...newsItems].sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    // Handle invalid dates
    if (isNaN(dateA) || isNaN(dateB)) {
      return 0; // Keep original order if dates are invalid
    }
    return dateB - dateA; // Descending order (newest first)
  });
  
  // Only display the three most recent news items
  const recentNewsItems = sortedNewsItems.slice(0, 3);

  // Define carouselImages with translations inside the component
  const carouselImages = [
    {
      src: '/images/main_page_carousel/c1.webp',
      alt: t('carousel.captions.science'),
      caption: t('carousel.captions.science')
    },
    {
      src: '/images/main_page_carousel/c2.webp',
      alt: t('carousel.captions.artists'),
      caption: t('carousel.captions.artists')
    },
    {
      src: '/images/main_page_carousel/c4.webp',
      alt: t('carousel.captions.teamwork'),
      caption: t('carousel.captions.teamwork')
    },
    {
      src: '/images/main_page_carousel/c8.webp',
      alt: t('carousel.captions.reading'),
      caption: t('carousel.captions.reading')
    },
    {
      src: '/images/main_page_carousel/c10.webp',
      alt: t('carousel.captions.curiosity'),
      caption: t('carousel.captions.curiosity')
    },
    {
      src: '/images/main_page_carousel/c11.webp',
      alt: t('carousel.captions.mauritanian'),
      caption: t('carousel.captions.mauritanian')
    }
  ];

  return (
    <PageLayout>
      <SEO
        title={t('home.hero.title1') + ' ' + t('home.hero.title2') + ' ' + t('home.hero.title3') + ' | TLC International School'}
        description={t('home.welcome')}
        path="/"
        keywords="TLC International School, Mauritania, Nouakchott, American education, international school, preschool, elementary, middle school, high school, multilingual education"
        type="website"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "EducationalOrganization",
          "name": "TLC International School",
          "alternateName": "TLC School",
          "description": t('home.welcome'),
          "url": "https://www.tlcmauritania.org",
          "logo": "https://www.tlcmauritania.org/images/logos/tlc-logo-removebg-preview.png",
          "image": "https://www.tlcmauritania.org/images/tlc-social-share.jpg",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Nouakchott",
            "addressLocality": "Nouakchott",
            "addressCountry": "MR"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+222-45-25-16-85",
            "contactType": "admissions",
            "availableLanguage": ["English", "French", "Arabic"]
          },
          "sameAs": [
            "https://www.facebook.com/tlcmauritania",
            "https://www.instagram.com/tlcmauritania"
          ],
          "foundingDate": "2006",
          "educationalLevel": ["Preschool", "Elementary", "Middle School", "High School"],
          "hasCredential": "Middle States Association Accreditation"
        }}
      />
      {/* Top Announcement Banner */}
      <div className="relative bg-gradient-to-r from-tlc-navy via-[#16294d] to-tlc-navy border-b border-tlc-gold/25">
        <div className="flex items-center justify-center gap-3 md:gap-5 py-2.5 px-4">
          <span className="hidden sm:block h-px w-8 md:w-14 bg-gradient-to-r from-transparent to-tlc-gold/70"></span>
          <span className="text-tlc-gold text-[11px] md:text-sm font-medium uppercase tracking-[0.22em] md:tracking-[0.3em] whitespace-nowrap">
            {t('home.hero.joyfulTagline', 'Over 20 Years of Joyful Learning')}
          </span>
          <span className="hidden sm:block h-px w-8 md:w-14 bg-gradient-to-l from-transparent to-tlc-gold/70"></span>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-tlc-navy via-tlc-navy to-blue-900 min-h-[90vh] flex items-center py-12 md:py-0">
        {/* Background pattern */}
        <div className="absolute inset-0 z-0 opacity-20">
          <div className="absolute top-0 -left-4 w-80 h-80 bg-tlc-gold rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
          <div className="absolute top-60 -right-4 w-80 h-80 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
          <div className="absolute bottom-20 left-40 w-80 h-80 bg-indigo-400 rounded-full mix-blend-multiply filter blur-3xl animate-pulse" style={{ animationDelay: '4s' }}></div>
        </div>
        
        <Container className="relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="inline-block px-4 py-1 rounded-full bg-tlc-gold/20 text-tlc-gold text-sm font-medium mb-6">
                {t('home.hero.tagline')}
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-6xl font-extrabold tracking-tight text-white mb-6 md:mb-8">
                <div className="leading-[1.1] mb-1">
                  <span className="text-white">{t('home.hero.title1')}</span>
                </div>
                <div className="leading-[1.1] mb-1">
                  <span className="text-tlc-gold">{t('home.hero.title2')}</span>
                </div>
                <div className="leading-[1.1]">
                  <span className="text-white">{t('home.hero.title3')}</span>
                </div>
              </h1>
              <p className="text-lg md:text-xl text-white/90 mb-6 md:mb-8 max-w-xl">
                {t('home.hero.description')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <LocalizedLink to="/academics">
                  <Button variant="primary" size="lg" className="w-full sm:w-auto group">
                    <span>{t('home.hero.explorePrograms')}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </Button>
                </LocalizedLink>
                <LocalizedLink to="/admissions">
                  <Button variant="secondary" size="lg" className="w-full sm:w-auto">
                    {t('home.hero.applyNow')}
                  </Button>
                </LocalizedLink>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8 lg:mt-0 block"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-tlc-gold to-tlc-gold/70 rounded-2xl blur translate-x-2 translate-y-2"></div>
                <div className="relative overflow-hidden rounded-2xl border-2 border-white/20 shadow-2xl">
                  <picture>
                    <source srcSet="/images/campus/exterior.webp" type="image/webp" />
                    <img
                      src="/images/campus/exterior.jpg"
                      alt="TLC School Campus"
                      className="w-full h-full object-cover aspect-[4/3]"
                      fetchPriority="high"
                      loading="eager"
                    />
                  </picture>
                </div>
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Key Outcomes Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-tlc-beige/30 relative overflow-hidden">
        {/* Decorative accents */}
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute -top-10 right-10 w-72 h-72 rounded-full bg-tlc-gold/10 blur-3xl"></div>
          <div className="absolute bottom-0 -left-10 w-72 h-72 rounded-full bg-tlc-navy/5 blur-3xl"></div>
        </div>
        <Container className="relative z-10">
          <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
            <span className="inline-block px-4 py-1 rounded-full bg-tlc-navy/5 text-tlc-navy text-sm font-medium mb-4">
              {t('home.outcomes.tagline', 'Proven Results')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-tlc-navy mb-3">
              {t('home.outcomes.title', 'Outcomes That Define Success')}
            </h2>
            <div className="h-1 w-20 bg-tlc-gold mx-auto rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {[
              { value: 100, title: t('home.outcomes.matriculation', 'University Matriculation'), desc: t('home.outcomes.matriculationDesc', 'Every graduate continues on to a university') },
              { value: 89, title: t('home.outcomes.apPassRate', 'AP Exam Pass Rate'), desc: t('home.outcomes.apPassRateDesc', 'Students earning a passing score on AP exams') },
              { value: 40, title: t('home.outcomes.advancedDegree', 'Advanced Degrees'), desc: t('home.outcomes.advancedDegreeDesc', 'Alumni who pursue a master\'s or higher') },
            ].map((item, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <div className="group relative h-full bg-white rounded-2xl border border-gray-100 shadow-md hover:shadow-xl transition-all duration-300 p-8 text-center hover:-translate-y-1 overflow-hidden">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 h-1 w-16 bg-tlc-gold rounded-b-full"></div>
                  <div className="text-5xl md:text-6xl font-extrabold text-tlc-gold mb-3 tracking-tight">
                    <CountUp end={item.value} suffix="%" />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-tlc-navy mb-2">{item.title}</h3>
                  <p className="text-sm md:text-base text-gray-600 leading-relaxed">{item.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </Container>
      </section>

      {/* Graduation Video Section */}
      <section className="py-12 md:py-16 bg-white relative overflow-hidden">
        <Container>
          <AnimatedSection>
            <div className="text-center max-w-3xl mx-auto mb-8">
              <motion.h2 
                className="text-3xl font-bold text-tlc-navy mb-3"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                {t('graduation.highlight.title', 'Graduation Ceremony')}
              </motion.h2>
              <motion.p 
                className="text-lg text-gray-600"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                {t('graduation.highlight.subtitle', 'Celebrating our students\' achievements and looking forward to their bright futures')}
              </motion.p>
            </div>

            <motion.div 
              className="mb-10 max-w-4xl mx-auto"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
            >
              <VideoPlayer 
                src="/videos/graduation_compressed.mp4" 
                caption={t('graduation.highlight.videoCaption', 'TLC School Graduation Ceremony')}
                autoPlay={true}
                loop={true}
                muted={true}
                controls={true}
                className="max-w-4xl mx-auto"
              />
            </motion.div>
          </AnimatedSection>
        </Container>
      </section>

      {/* Mission Section */}
      <section className="py-16 md:py-20 lg:py-24 bg-tlc-beige/30 relative overflow-hidden">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
            <AnimatedSection>
              <div className="relative mb-8 lg:mb-0">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-tlc-navy to-blue-600 rounded-2xl blur opacity-50"></div>
                <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                  <img
                    src="/images/elementary_school/k3.jpeg"
                    alt="Students in classroom"
                    className="w-full h-full object-cover aspect-[4/3]"
                  />
                </div>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={0.2}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-tlc-navy mb-4 md:mb-6">
                {t('home.missionVision.title')}
              </h2>
              <p className="text-lg md:text-xl text-gray-600 mb-6 md:mb-8">
                {t('home.missionVision.description')}
              </p>
              <div className="mb-6 md:mb-8 italic text-gray-700 border-l-4 border-tlc-gold pl-4 py-2">
                {t('home.missionVision.tagline')}
              </div>
              <LocalizedLink to="/about">
                <Button 
                  variant="primary" 
                  size="lg" 
                  className="w-full sm:w-auto"
                  aria-label="Learn more about TLC's mission and vision"
                >
                  {t('home.missionVision.learnMore')}
                </Button>
              </LocalizedLink>
            </AnimatedSection>
          </div>
        </Container>
      </section>

      {/* Key Features Section */}
      <section className="py-16 md:py-20 lg:py-28 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5 z-0">
          <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 0 10 L 40 10 M 10 0 L 10 40" stroke="currentColor" strokeWidth="0.5" fill="none" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
        
        <Container className="relative z-10">
          <AnimatedSection>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-block px-3 py-1 rounded-full bg-tlc-navy/10 text-tlc-navy text-sm font-medium mb-4">
                {t('home.features.tagline')}
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-tlc-navy mb-6">
                {t('home.features.title')}
              </h2>
              <div className="h-1 w-20 bg-tlc-gold mx-auto mb-6"></div>
              <p className="text-lg md:text-xl text-gray-700">
                {t('home.features.description')}
              </p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {features.map((feature, index) => (
              <AnimatedSection key={feature.name} delay={index * 0.1}>
                <div className="h-full group relative bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                  <div className={`absolute top-0 left-0 w-full h-2 bg-gradient-to-r ${feature.color}`}></div>
                  <div className="p-8 md:p-10">
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-r from-tlc-navy to-blue-700 flex items-center justify-center mb-6 transform group-hover:scale-110 transition-transform duration-300">
                      <feature.icon className="h-8 w-8 text-white" aria-hidden="true" />
                    </div>
                    <h3 className="text-2xl font-bold text-tlc-navy mb-4 group-hover:text-tlc-gold transition-colors duration-300">{feature.name}</h3>
                    <p className="text-gray-600 mb-6">{feature.description}</p>
                    <LocalizedLink 
                      to={feature.link} 
                      className="inline-flex items-center text-tlc-navy font-medium group-hover:text-tlc-gold transition-colors duration-300"
                      aria-label={`Learn more about ${feature.name}`}
                    >
                        {t('home.features.learnMore')} {feature.name}
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </LocalizedLink>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </Container>
      </section>

      {/* Image Carousel Section */}
      <section className="py-16 bg-white relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-tlc-navy/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-tlc-gold/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3"></div>
        
        <div className="absolute inset-0 opacity-5">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="dot-pattern" width="40" height="40" patternUnits="userSpaceOnUse">
                <circle cx="2" cy="2" r="1" fill="#1e467a" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#dot-pattern)" />
          </svg>
        </div>
        
        <Container className="relative z-10">
          <AnimatedSection>
            <div className="text-center mb-10">
              <motion.span 
                initial={{ opacity: 0, y: -10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="inline-block px-3 py-1 rounded-full bg-tlc-navy/10 text-tlc-navy text-sm font-medium mb-4"
              >
                {t('home.campus.tagline')}
              </motion.span>
              <motion.h2 
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="text-3xl md:text-4xl font-bold text-tlc-navy mb-3"
              >
                {t('home.campus.title')}
              </motion.h2>
              <motion.div 
                initial={{ width: 0 }}
                whileInView={{ width: 80 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="h-1 bg-tlc-gold mx-auto"
              ></motion.div>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            <div className="relative mt-12 mb-10">              
              <div className="sr-only">{t('home.campus.carouselDescription')}</div>
              <Swiper
                modules={[Autoplay, Pagination, Navigation, EffectFade]}
                effect="fade"
                spaceBetween={0}
                slidesPerView={1}
                centeredSlides={true}
                dir={i18n.dir()}
                autoplay={{
                  delay: 5000,
                  disableOnInteraction: false,
                  reverseDirection: i18n.dir() === 'rtl',
                }}
                pagination={{
                  clickable: true,
                  dynamicBullets: true,
                  renderBullet: function (index: number, className: string) {
                    return `<span class="${className}" aria-label="${t('carousel.pagination', { index: index + 1 })}"></span>`;
                  },
                }}
                navigation={true}
                loop={true}
                className="rounded-2xl overflow-hidden home-carousel"
                style={{ height: '550px' }}
                onSwiper={(swiper: SwiperType) => {
                  swiperRef.current = swiper;
                }}
                onBeforeInit={(swiper: SwiperType) => {
                  // Apply RTL-specific initializations if needed
                  if (i18n.dir() === 'rtl') {
                    // Force a browser reflow to ensure RTL layout is applied
                    setTimeout(() => {
                      swiper.update();
                    }, 0);
                  }
                }}
                onSlideChange={(swiper: SwiperType) => {
                  // Slide transition happened
                }}
              >
                {carouselImages.map((image, index) => (
                  <SwiperSlide key={index}>
                    <div className="relative h-full w-full">
                      <img 
                        src={image.src}
                        alt={image.alt}
                        className="w-full h-full object-cover"
                        loading={index === 0 ? 'eager' : 'lazy'}
                      />
                      <div className="image-overlay"></div>
                      <div className="absolute bottom-0 left-0 right-0 z-10 slide-caption">
                        <div className={`flex items-center gap-3 ${i18n.dir() === 'rtl' ? 'flex-row-reverse' : ''}`}>
                          <div className="w-1 h-12 bg-tlc-gold rounded-full"></div>
                          <h3 className="caption-heading text-xl md:text-2xl font-semibold text-white leading-snug">
                            {image.caption}
                          </h3>
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>

            <div className="text-center mt-12">
              <LocalizedLink to="/contact">
                <Button variant="outline" size="lg" className="group hover:bg-tlc-navy hover:text-white transition-all duration-300 border-2 shadow-lg relative overflow-hidden">
                  <span className="relative z-10">{t('home.campus.contact')}</span>
                  <div className="absolute inset-0 bg-tlc-gold origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2 relative z-10 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </Button>
              </LocalizedLink>
            </div>
          </AnimatedSection>
        </Container>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-20 bg-tlc-navy relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-20">
          <div className="absolute -right-20 top-20 w-64 h-64 rounded-full bg-tlc-gold/40 blur-3xl"></div>
          <div className="absolute -left-20 bottom-20 w-64 h-64 rounded-full bg-blue-500/30 blur-3xl"></div>
        </div>
        <Container className="relative z-10">
          <div className="text-center mb-10 md:mb-14">
            <span className="inline-block px-3 py-1 rounded-full bg-white/10 text-white text-sm font-medium mb-4">
              {t('home.stats.tagline')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">{t('home.stats.title')}</h2>
            <div className="h-1 w-20 bg-tlc-gold mx-auto"></div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {stats.map((stat, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <div className="flex flex-col items-center text-center">
                  <div className="text-4xl md:text-5xl font-bold text-tlc-gold mb-2"><CountUp end={stat.value} suffix={stat.suffix} /></div>
                  <div className="h-px w-12 bg-white/30 mb-2"></div>
                  <div className="text-sm md:text-base text-white/90">{stat.label}</div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </Container>
      </section>

      {/* Innovative Learning Section */}
      <section className="py-16 md:py-20 lg:py-24 bg-tlc-beige/20">
        <Container>
          <AnimatedSection>
            <div className="text-center max-w-4xl mx-auto mb-10 md:mb-14">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-tlc-navy mb-4 md:mb-6">
                {t('home.innovativeLearning.title')}
              </h2>
              <div className="mx-auto h-1 w-24 bg-tlc-gold mb-6 md:mb-8"></div>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
                {t('home.innovativeLearning.description1')}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div className="order-2 md:order-1">
                <p className="text-gray-700 mb-6">
                  {t('home.innovativeLearning.description2')}
                </p>
                <p className="text-gray-700">
                  {t('home.innovativeLearning.description3')}
                </p>
              </div>
              
              <div className="order-1 md:order-2">
                <div className="relative">
                  <div className="absolute -inset-1 bg-gradient-to-r from-tlc-gold/30 to-tlc-navy/30 rounded-2xl blur opacity-70"></div>
                  <div className="relative overflow-hidden rounded-xl border border-gray-200">
                    <img
                      src="/images/elementary_school/elementary_1.jpeg"
                      alt="TLC Students Learning"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </Container>
      </section>

      {/* Photo Gallery */}
      <section className="py-20 lg:py-28 bg-white relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-5">
          <div className="absolute top-0 left-0 w-full h-full">
            <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 800 800">
              <rect fill="none" stroke="currentColor" strokeWidth="1" strokeOpacity="0.15" x="30" y="30" width="100" height="100" rx="10"></rect>
              <rect fill="none" stroke="currentColor" strokeWidth="1" strokeOpacity="0.15" x="30" y="160" width="100" height="100" rx="10"></rect>
              <rect fill="none" stroke="currentColor" strokeWidth="1" strokeOpacity="0.15" x="30" y="290" width="100" height="100" rx="10"></rect>
              <rect fill="none" stroke="currentColor" strokeWidth="1" strokeOpacity="0.15" x="30" y="420" width="100" height="100" rx="10"></rect>
              <rect fill="none" stroke="currentColor" strokeWidth="1" strokeOpacity="0.15" x="160" y="30" width="100" height="100" rx="10"></rect>
              <rect fill="none" stroke="currentColor" strokeWidth="1" strokeOpacity="0.15" x="160" y="160" width="100" height="100" rx="10"></rect>
              <rect fill="none" stroke="currentColor" strokeWidth="1" strokeOpacity="0.15" x="160" y="290" width="100" height="100" rx="10"></rect>
              <rect fill="none" stroke="currentColor" strokeWidth="1" strokeOpacity="0.15" x="160" y="420" width="100" height="100" rx="10"></rect>
            </svg>
          </div>
        </div>
        
        <Container className="relative z-10">
          <AnimatedSection>
            <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
              <span className="inline-block px-3 py-1 rounded-full bg-tlc-navy/10 text-tlc-navy text-sm font-medium mb-4">
                {t('home.studentLife.tagline')}
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-tlc-navy mb-4">
                {t('home.studentLife.title')}
              </h2>
              <div className="h-1 w-20 bg-tlc-gold mx-auto mb-6"></div>
              <p className="text-lg text-gray-700">
                {t('home.studentLife.description')}
              </p>
            </div>
          </AnimatedSection>
          
          <div className="grid grid-cols-12 gap-4 md:gap-6">
            <AnimatedSection delay={0.1} className="col-span-12 md:col-span-8">
              <div className="relative h-64 md:h-96 group overflow-hidden rounded-2xl shadow-lg">
                <img 
                  src="/images/elementary_school/k1.jpg" 
                  alt="Students collaborating" 
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex flex-col justify-end p-6">
                  <h3 className="text-white text-xl font-semibold mb-2">{t('home.studentLife.features.collaborative.title')}</h3>
                  <p className="text-white/80 text-sm">{t('home.studentLife.features.collaborative.description')}</p>
                </div>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={0.2} className="col-span-6 md:col-span-4">
              <div className="relative h-64 md:h-96 group overflow-hidden rounded-2xl shadow-lg">
                <img 
                  src="/images/high_school/hs4.jpg" 
                  alt="Science class" 
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex flex-col justify-end p-6">
                  <h3 className="text-white text-xl font-semibold mb-2">{t('home.studentLife.features.excellence.title')}</h3>
                  <p className="text-white/80 text-sm">{t('home.studentLife.features.excellence.description')}</p>
                </div>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={0.3} className="col-span-6 md:col-span-4">
              <div className="relative h-64 group overflow-hidden rounded-2xl shadow-lg">
                <img 
                  src="/images/elementary_school/k2.jpg" 
                  alt="School activities" 
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex flex-col justify-end p-6">
                  <h3 className="text-white text-xl font-semibold mb-2">{t('home.studentLife.features.earlyLearning.title')}</h3>
                  <p className="text-white/80 text-sm">{t('home.studentLife.features.earlyLearning.description')}</p>
                </div>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={0.4} className="col-span-6 md:col-span-4">
              <div className="relative h-64 group overflow-hidden rounded-2xl shadow-lg">
                <img 
                  src="/images/middle_school/ms2.jpeg" 
                  alt="Student activities" 
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex flex-col justify-end p-6">
                  <h3 className="text-white text-xl font-semibold mb-2">{t('home.studentLife.features.activities.title')}</h3>
                  <p className="text-white/80 text-sm">{t('home.studentLife.features.activities.description')}</p>
                </div>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={0.5} className="col-span-6 md:col-span-4">
              <div className="relative h-64 group overflow-hidden rounded-2xl shadow-lg">
                <img 
                  src="/images/robotics/r4.jpeg" 
                  alt="Robotics Team" 
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex flex-col justify-end p-6">
                  <h3 className="text-white text-xl font-semibold mb-2">{t('home.studentLife.features.robotics.title')}</h3>
                  <p className="text-white/80 text-sm">{t('home.studentLife.features.robotics.description')}</p>
                </div>
              </div>
            </AnimatedSection>
          </div>
          
          <div className="text-center mt-10 md:mt-12">
            <LocalizedLink to="/activities">
              <Button variant="primary" size="lg" className="group">
                <span>{t('home.studentLife.exploreButton')}</span>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Button>
            </LocalizedLink>
          </div>
        </Container>
      </section>

      {/* News & Events Section */}
      <section className="py-16 lg:py-24 bg-white">
        <Container>
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
            {/* News Section */}
            <AnimatedSection>
              <div className="flex items-center mb-6 md:mb-8">
                <NewspaperIcon className="h-8 w-8 md:h-10 md:w-10 text-tlc-navy mr-3 md:mr-4" />
                <h2 className="text-2xl md:text-3xl font-bold text-tlc-navy">{t('home.news.title')}</h2>
              </div>
              
              <div className="space-y-6 md:space-y-8">
                {recentNewsItems.map((item, index) => (
                  <div key={index} className={`bg-white rounded-xl shadow-md overflow-hidden flex flex-col sm:flex-row ${
                    item.isLatest ? 'ring-4 ring-tlc-gold ring-opacity-50 shadow-lg' : ''
                  }`}>
                    {item.video && (
                      <div className="relative w-full flex-shrink-0 h-48 sm:h-auto sm:w-1/3">
                        <video
                          src={item.video}
                          controls
                          autoPlay
                          loop
                          muted
                          playsInline
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      </div>
                    )}
                    {shouldShowImage(item) && !item.video && (
                      <div className={`w-full flex-shrink-0 ${item.id === 2 ? 'sm:w-1/4 h-40 sm:h-48' : 'sm:w-1/3 h-48 sm:h-auto'}`}>
                        <img 
                          src={item.image} 
                          alt={item.title} 
                          className="h-full w-full object-cover object-top"
                        />
                      </div>
                    )}
                    <div className={`p-4 md:p-5 w-full ${item.video ? 'sm:w-2/3' : shouldShowImage(item) ? (item.id === 2 ? 'sm:w-3/4' : 'sm:w-2/3') : 'w-full'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        {item.isLatest && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-tlc-gold text-tlc-navy">
                            Latest
                          </span>
                        )}
                        <p className="text-sm text-gray-500">{item.date}</p>
                      </div>
                      <h3 className={`text-lg font-semibold mb-2 ${
                        item.isLatest ? 'text-tlc-gold' : 'text-tlc-navy'
                      }`}>{item.title}</h3>
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">{item.summary}</p>
                      <LocalizedLink to={`/news#article-${item.id}`} className="text-sm font-medium text-tlc-navy hover:underline inline-flex items-center">
                        {t('home.news.readMore')} <span className="ml-1">→</span>
                      </LocalizedLink>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 md:mt-8 text-center">
                <LocalizedLink to="/news">
                <Button variant="outline" size="md">{t('home.news.viewAll')}</Button>
                </LocalizedLink>
              </div>
            </AnimatedSection>
            
            {/* Events Section */}
            <AnimatedSection delay={0.2}>
              <div className="flex items-center mb-6 md:mb-8">
                <CalendarIcon className="h-8 w-8 md:h-10 md:w-10 text-tlc-navy mr-3 md:mr-4" />
                <h2 className="text-2xl md:text-3xl font-bold text-tlc-navy">{t('home.events.title')}</h2>
              </div>
              
              <div className="space-y-4 md:space-y-5">
                {recentEventsList.map((event, index) => (
                  <div key={index} className="bg-white rounded-xl shadow-md p-4 md:p-5 border-l-4 border-tlc-gold">
                    <h3 className="text-lg md:text-xl font-semibold text-tlc-navy mb-1.5">{event.title}</h3>
                    <div className="flex items-center text-gray-500 mb-1">
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      <span className="text-sm">{event.date}</span>
                    </div>
                    <div className="flex items-center text-gray-500 mb-1.5">
                      <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-sm">{event.time}</span>
                    </div>
                    <div className="flex items-start text-gray-500 mb-1.5">
                      <svg className="h-4 w-4 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-sm">{event.location}</span>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 md:mt-8 text-center">
                <LocalizedLink to="/news">
                <Button variant="outline" size="md">{t('home.events.viewCalendar')}</Button>
                </LocalizedLink>
              </div>
            </AnimatedSection>
          </div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-tlc-navy to-blue-900 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute top-0 right-0 w-80 h-80 bg-tlc-gold/20 rounded-full mix-blend-overlay filter blur-3xl opacity-70"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-400/30 rounded-full mix-blend-overlay filter blur-3xl opacity-70"></div>
          
          {/* Abstract pattern */}
          <div className="absolute inset-0 opacity-10">
            <svg width="100%" height="100%" viewBox="0 0 800 800" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="smallGrid" width="20" height="20" patternUnits="userSpaceOnUse">
                  <path d="M 20 0 L 0 0 0 20" fill="none" stroke="white" strokeWidth="0.5" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#smallGrid)" />
            </svg>
          </div>
        </div>
        
        <Container className="relative z-10">
          <div className="rounded-3xl bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm p-8 md:p-10 lg:p-14 border border-white/10 shadow-2xl">
            <div className="max-w-3xl mx-auto">
              <div className="flex flex-col md:flex-row items-center mb-8 md:mb-10">
                <div className="bg-tlc-gold/20 backdrop-blur-sm p-4 rounded-full mb-6 md:mb-0 md:mr-8">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-tlc-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div className="text-center md:text-left">
                  <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
                {t('home.cta.title')}
              </h2>
                  <div className="h-1 w-20 bg-tlc-gold mb-4 mx-auto md:mx-0"></div>
                  <p className="text-lg md:text-xl text-white/80">
                {t('home.cta.description')}
              </p>
            </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                <LocalizedLink to="/admissions">
                  <Button variant="primary" size="lg" className="w-full sm:w-auto group">
                    <span>{t('home.cta.applyNow')}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </Button>
                </LocalizedLink>
                <LocalizedLink to="/contact">
                  <Button variant="secondary" size="lg" className="w-full sm:w-auto">
                    {t('home.cta.contactUs')}
                  </Button>
                </LocalizedLink>
              </div>
              
              <div className="mt-8 md:mt-10 pt-8 md:pt-10 border-t border-white/10 grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
                <div>
                  <p className="text-white font-medium mb-1">{t('home.cta.email')}</p>
                  <a href="mailto:info@tlcmauritania.org" className="text-tlc-gold hover:underline">
                    {t('home.cta.emailAddress')}
                  </a>
              </div>
                <div>
                  <p className="text-white font-medium mb-1">{t('home.cta.phone')}</p>
                  <a href="tel:+222 46041532" className="text-tlc-gold hover:underline">
                    {t('home.cta.phoneNumber')}
                  </a>
              </div>
                <div>
                  <p className="text-white font-medium mb-1">{t('home.cta.location')}</p>
                  <p className="text-tlc-gold">{t('home.cta.locationText')}</p>
          </div>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </PageLayout>
  );
};

export default HomePage; 